import { useState, useEffect } from 'react';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import { fetchFileContent, fetchRepoVariable } from '../shared';

interface UseRepoManifestResult {
  availableEnvironments: string[];
  defaultBudget: number | null;
  defaultLifetimeBudget: number | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch manifest data for a BU repository.
 *
 * Tries backstage-manifest.json first for environments and default_budget,
 * falls back to the ENVIRONMENTS GitHub Actions variable for environments.
 */
export function useRepoEnvironments(
  owner: string,
  repo: string | undefined,
): UseRepoManifestResult {
  const [availableEnvironments, setAvailableEnvironments] = useState<string[]>([]);
  const [defaultBudget, setDefaultBudget] = useState<number | null>(null);
  const [defaultLifetimeBudget, setDefaultLifetimeBudget] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const githubAuth = useApi(githubAuthApiRef);

  useEffect(() => {
    if (!repo) {
      setAvailableEnvironments([]);
      setDefaultBudget(null);
      setDefaultLifetimeBudget(null);
      setError(null);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      setDefaultBudget(null);
      setDefaultLifetimeBudget(null);
      try {
        const token = await githubAuth.getAccessToken(['repo']);
        let foundEnvs = false;

        // Try backstage-manifest.json first
        try {
          const text = await fetchFileContent(token, owner, repo, 'backstage-manifest.json');
          const manifest = JSON.parse(text);
          if (manifest.environments && Array.isArray(manifest.environments)) {
            setAvailableEnvironments(manifest.environments);
            foundEnvs = true;
          }
          if (manifest.default_budget != null) {
            const parsed = Number(manifest.default_budget);
            if (!isNaN(parsed)) {
              setDefaultBudget(parsed);
            }
          }
          if (manifest.default_lifetime_budget != null) {
            const parsed = Number(manifest.default_lifetime_budget);
            if (!isNaN(parsed)) {
              setDefaultLifetimeBudget(parsed);
            }
          }
        } catch {
          // backstage-manifest.json not found or invalid — fall back
        }

        // Fall back to ENVIRONMENTS GitHub Actions variable
        if (!foundEnvs) {
          const envs = await fetchRepoVariable(token, owner, repo, 'ENVIRONMENTS');
          setAvailableEnvironments(envs);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch repo manifest');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [githubAuth, owner, repo]);

  return { availableEnvironments, defaultBudget, defaultLifetimeBudget, loading, error };
}
